import React, { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { FaInfoCircle, FaImage, FaPaintBrush, FaVideo, FaCoins, FaShoppingCart } from "react-icons/fa";
import { FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import { motion } from "framer-motion";
import { useUser, useAuth, useClerk } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import "./CreateImage.css";
import GeneratedContent from '../components/GeneratedContent';
import TacoStudioEditor from '../components/TacoStudioEditor';
import TacoStudioWelcomeSplash from '../components/TacoStudioWelcomeSplash';
import { useUserDetails } from '../context/UserContext';
import { PAGE_TYPES, getSessionKey, setSessionData, getSessionData, removeSessionData } from '../utils/sessionStorageHelper';

/*
  TacoStudio Component
  
  Unified component that combines CreateImage and CreateVideo functionality.
  Users can switch between Image and Video modes using a radio button selector.
  
  This component reuses logic from both CreateImage and CreateVideo components,
  with conditional rendering and behavior based on the selected content type.
*/

// Custom Header Component for TacoStudio - MOVED OUTSIDE to prevent re-renders
const CreateContentHeader = React.memo(({ creditInfo, onHelpClick, hasApiCredits, contentType }) => {
  const { isSignedIn, isLoaded } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { userDetails } = useUserDetails();
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProfileClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleUserProfileClick = () => {
    window.open(process.env.REACT_APP_USER_PROFILE, '_blank');
    setIsDropdownOpen(false);
  };

  const handleLogout = () => {
    signOut();
    setIsDropdownOpen(false);
  };


  // Determine icon and text based on contentType
  // const ContentIcon = contentType === "image" ? FaPaintBrush : FaVideo;
  const contentTypeLabel = contentType === "image" ? "Image" : "Video";

  return (
      <header className="fixed top-0 left-0 right-0 z-50 w-full">
      {/* Glass Header Container */}
      <div className="glass-effect border-b border-white/5 backdrop-blur-2xl bg-black/10">
        <div className="max-w-7xl mx-auto px-2 sm:px-3 md:px-4 lg:px-6 py-1">
          <div className="relative flex items-center justify-between">
            
            {/* Left Section - Logo and Taco */}
            {!isSignedIn && (
              <div className="flex items-center gap-1 lg:gap-1.5">
                {/* Logo */}
                <img 
                  src="/logo.png" 
                  alt="Taco Logo" 
                  className="w-5 h-5 sm:w-6 sm:h-6"
                />
                
                {/* Taco Text */}
                <h1 className="text-sm sm:text-base lg:text-lg font-bold">
                  <span className="gradient-text">Taco</span>
                </h1>
              </div>
            )}
            
            {/* Centered Title - Desktop */}
            <div className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 hidden sm:flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-lg bg-white/5 border border-white/5 flex items-center justify-center">
                <FaPaintBrush className="text-white/70 text-sm" />
              </div>
              <div className="text-white/80 text-sm font-medium tracking-wide">Prototype Studio</div>
            </div>

            {/* Mobile Title - Positioned to avoid sidebar conflicts */}
            <div className="sm:hidden flex items-center gap-1.5 ml-12">
              <div className="w-6 h-6 rounded-lg bg-white/5 border border-white/5 flex items-center justify-center">
                <FaPaintBrush className="text-white/70 text-xs" />
              </div>
              <div className="text-white/80 text-sm font-medium tracking-wide">Prototype Studio</div>
            </div>

            {/* Right Section - Auth Buttons or Profile */}
            <div className="flex items-center gap-1 sm:gap-1.5 ml-auto">
              {isLoaded && (
                <>
                  {isSignedIn ? (
                    /* Profile Section with Dropdown - When Signed In */
                    <div className="relative" ref={dropdownRef}>
                      <div 
                        onClick={handleProfileClick}
                        className="flex items-center gap-1 sm:gap-1.5 cursor-pointer transition-all duration-300 group transition-all duration-300 px-1 sm:px-1.5 md:px-2 py-0.5 rounded-full hover:bg-white/10"
                      >
                        {/* User Avatar */}
                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full overflow-hidden border border-gray-300 group-hover:border-white transition-colors duration-300">
                          {user?.imageUrl ? (
                            <img 
                              src={user.imageUrl} 
                              alt="Profile" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                              </svg>
                            </div>
                          )}
                        </div>
                        
                        {/* User Name and Plan Info */}
                        <div className="flex flex-col items-start">
                          <span className="text-xs font-medium text-gray-200 group-hover:text-white transition-colors duration-300 leading-tight">
                            {user?.firstName} {user?.lastName}
                          </span>
                          {/* Plan info - more compact on mobile */}
                          <div className="flex items-center gap-0.5 sm:gap-1 mt-0 leading-tight">
                            <span className={`text-xs font-medium ${parseFloat(hasApiCredits ? (creditInfo?.credit_left_amt ?? '0') : (userDetails?.credit_left_amt ?? '0')) > 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {parseFloat(hasApiCredits ? (creditInfo?.credit_left_amt ?? '0') : (userDetails?.credit_left_amt ?? '0')).toFixed(2)} credits
                            </span>
                          </div>
                        </div>
                        
                        {/* Dropdown Arrow */}
                        <svg 
                          className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-300 group-hover:text-white transition-colors duration-300" 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                      
                      {/* Dropdown Menu */}
                      {isDropdownOpen && (
                        <div className="absolute right-0 mt-1 w-40 sm:w-48 md:w-56 bg-gray-900/95 backdrop-blur-xl border border-gray-700/50 rounded-lg shadow-xl overflow-hidden z-50">
                          {/* User Info Section */}
                          <div className="px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-800">
                            <div className="flex items-center gap-2 sm:gap-3">
                              <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-600 flex-shrink-0">
                                {user?.imageUrl ? (
                                  <img 
                                    src={user.imageUrl} 
                                    alt="Profile" 
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                    </svg>
                                  </div>
                                )}
                              </div>
                              <div>
                                <p className="text-white font-medium text-sm">
                                  {user?.firstName} {user?.lastName}
                                </p>
                                <p className="text-gray-400 text-xs">
                                  {user?.primaryEmailAddress?.emailAddress}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="py-1 sm:py-2">
                            <button
                              onClick={() => {
                                navigate('/billing');
                                setIsDropdownOpen(false);
                              }}
                              className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors duration-300 flex items-center gap-2 text-xs sm:text-sm"
                            >
                              <FaShoppingCart className="text-gray-300" />
                              Buy More Credits
                            </button>
                            <button
                              onClick={() => {
                                window.open('mailto:manesh.pai@visualtaco.com?subject=Hi there!', '_blank');
                                setIsDropdownOpen(false);
                              }}
                              className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors duration-300 flex items-center gap-2 text-xs sm:text-sm"
                            >
                              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              Contact Us
                            </button>
                            <button
                              onClick={handleUserProfileClick}
                              className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors duration-300 flex items-center gap-2 text-xs sm:text-sm"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              User Profile
                            </button>
                            <button
                              onClick={handleLogout}
                              className="w-full px-3 sm:px-4 py-1.5 sm:py-2 text-left text-gray-300 hover:text-white hover:bg-gray-800/50 transition-colors duration-300 flex items-center gap-2 text-xs sm:text-sm"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                              </svg>
                              Log out
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Log In and Sign Up Buttons - When Not Signed In */
                    <>
                      {/* Log In Button */}
                      <a 
                        href="/login" 
                        className="text-gray-300 hover:text-white transition-colors duration-300 px-1.5 sm:px-2 py-0.5 text-xs font-medium inline-block"
                      >
                        Log In
                      </a>
                      
                      {/* Sign Up Button */}
                      <a 
                        href="/sign-up" 
                        className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-2 sm:px-3 py-0.5 rounded-full text-xs font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl inline-block"
                      >
                        Sign Up
                      </a>
                    </>
                  )}
                </>
              )}
              {/* Help button - always visible */}
              <button
                onClick={onHelpClick}
                className="ml-1 inline-flex items-center justify-center w-6 h-6 rounded-full border border-white/20 text-gray-300 hover:text-white hover:bg-white/10 transition"
                title="Help"
                aria-label="Show help tour"
              >
                <FaInfoCircle className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Top glow effect */}
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent"></div>
    </header>
  );
});

function TacoStudio() {
  // ============================================
  // CONTENT TYPE SELECTOR - NEW FOR TACOSTUDIO
  // ============================================
  // Initialize contentType from sessionStorage or default to "image"
  const [contentType, setContentType] = useState(() => {
    try {
      const stored = sessionStorage.getItem('tacoStudio_contentType');
      return stored && ['image', 'video', 'upscale'].includes(stored) ? stored : 'image';
    } catch {
      return 'image';
    }
  });
  const PAGE_TYPE = PAGE_TYPES.STUDIO;
  
  // Persist contentType to sessionStorage whenever it changes
  useEffect(() => {
    try {
      sessionStorage.setItem('tacoStudio_contentType', contentType);
    } catch (e) {
      // Ignore storage errors
    }
  }, [contentType]);
  
  // Handle content type change
  const handleContentTypeChange = (newType) => {
    if (newType === contentType) return;
    
    // Only abort/reset if generation is NOT in progress
    // If generation is in progress, preserve the state and let it continue
    const isGenerationActive = isGeneratingContentRef.current || isGeneratingContent;
    
    if (!isGenerationActive) {
      // Clear any ongoing generation only if not actively generating
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Reset state only when not generating
      setIsGeneratingContent(false);
      setGenerateContentLabel("Generate Content");
    }
    // If generation is active, don't abort or reset - let it continue
    
    // Switch content type (always allowed, even during generation)
    setContentType(newType);
  };

  // Get userDetails from context (already imported via useUserDetails hook)
  const { userDetails, setUserDetails } = useUserDetails();

  // Utility function to safely store data in sessionStorage with quota management
  const safeSessionStorageSet = (key, data) => {
    return setSessionData(key, data, PAGE_TYPE);
  };

  // Utility function to safely get data from sessionStorage
  const safeSessionStorageGet = (key) => {
    return getSessionData(key, PAGE_TYPE);
  };

  // Utility function to compress images before storage
  const compressImageForStorage = (file, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        
        resolve({
          dataUrl: compressedDataUrl,
          name: file.name,
          type: file.type,
          size: file.size
        });
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Add state for business details warning
  const [showBusinessWarning, setShowBusinessWarning] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState('Catalog'); // Default style
  const [selectedEvent, setSelectedEvent] = useState(null); // Optional event

  // Check business details on component mount
  useEffect(() => {
    if (!userDetails?.business || 
        (!userDetails.business.name && 
        !userDetails.business.description && 
        !userDetails.business.location)) {
      setShowBusinessWarning(true);
    } else {
      setShowBusinessWarning(false);
    }
  }, [userDetails]);
  
  // ---------------------------
  // Component States
  // ---------------------------
  const { user } = useUser();
  const { getToken } = useAuth();

  const [selectedPlatform, setSelectedPlatform] = useState("");
  // Default post type differs: image can be "", video defaults to "video"
  const [artFormat, setArtFormat] = useState("");
  const [contentMode, setContentMode] = useState("");
  const [mediaSource, setMediaSource] = useState("");
  // video duration: image doesn't use it (uses constant 10), video uses state (default 5)
  const [reelDuration, setReelDuration] = useState(5);
  const [reelDurationError, setReelDurationError] = useState("");

  const [enableGenerateContentButton, setEnableGenerateContentButton] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [message, setMessage] = useState("");
  const [originalUploadedImages, setOriginalUploadedImages] = useState([]);
  const [referenceUploadedImages, setReferenceUploadedImages] = useState([]);
  const [aspectRatio, setAspectRatio] = useState("Portrait");
  const [generatedImgContent, setGeneratedImgContent] = useState(null);
  const [generatedVidContent, setGeneratedVidContent] = useState(null);
  const [contentGenerated, setContentGenerated] = useState(false);
  const [generateContentLabel, setGenerateContentLabel] = useState("Generate Content");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishStatus, setPublishStatus] = useState({ type: null, message: "" });
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const isGeneratingContentRef = useRef(false); // Ref to prevent concurrent requests
  const pollingActiveRef = useRef(false); // Ref to prevent duplicate polling loops
  const lastProcessedPostTimeRef = useRef(null); // Ref to track last processed post and prevent duplicate additions
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [createPoster, setCreatePoster] = useState(true);
  const [messageError, setMessageError] = useState("");
  const [creditInfo, setCreditInfo] = useState({ total_cost: 0, credit_left_tokens: 0, credit_left_amt: 0 });
  const [hasApiCredits, setHasApiCredits] = useState(false);
  // const messageTextareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const textboxFileInputRef = useRef(null);
  const errorPopupRef = useRef(null);
  const tacoStudioEditorRef = useRef(null);
  const [uploadError, setUploadError] = useState("");
  const [referenceUploadError, setReferenceUploadError] = useState("");
  const [ownImageUploadError, setOwnImageUploadError] = useState("");
  const [textboxUploadedImages, setTextboxUploadedImages] = useState([]);
  const [textboxUploadError, setTextboxUploadError] = useState("");
  const [isReferenceLoading, setIsReferenceLoading] = useState(false);
  const [contentTimestamp, setContentTimestamp] = useState("");
  const [apiError, setApiError] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const generatedContentRef = useRef(null);
  const abortControllerRef = useRef(null);
  const navigate = useNavigate();
  const [viewerIsOpen, setViewerIsOpen] = useState(false);
  const [currentLightboxImage, setCurrentLightboxImage] = useState(0);
  const [generatedImgHistory, setGeneratedImgHistory] = useState([]);
  const [sessionSelectedImageIndex, setSessionSelectedImageIndex] = useState(null);
  const [isAspectRatioExpanded, setIsAspectRatioExpanded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [totalTime, setTotalTime] = useState(20);
  // const [businessDetailsError, setBusinessDetailsError] = useState("");
  
  // Additional states for editor components
  const [creationInputMode, setCreationInputMode] = useState("freeText");
  const [selectedCreationOption, setSelectedCreationOption] = useState("create_image");
  const [selectedModel, setSelectedModel] = useState("free");
  const [showReferenceImageModal, setShowReferenceImageModal] = useState(false);
  const [isModelCollapsed, setIsModelCollapsed] = useState(false);
  const [isOutputCollapsed, setIsOutputCollapsed] = useState(false);
  
  // Model parameters state for DynamicModelForm
  const [modelParams, setModelParams] = useState({});
  const [modelFileObjects, setModelFileObjects] = useState({});
  

  
  const [isPageVisible, setIsPageVisible] = useState(true);
  const [isBackgroundGeneration, setIsBackgroundGeneration] = useState(false);
  const [generationJobId, setGenerationJobId] = useState(null);  
  // Common settings
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isLoadingFromLandingPage, setIsLoadingFromLandingPage] = useState(false);
  const [containerHeight, setContainerHeight] = useState(0);

  // Helper function to save generation state to sessionStorage
  const saveGenerationState = useCallback((state) => {
    try {
      const stateToSave = {
        isGeneratingContent: state.isGeneratingContent,
        timeRemaining: state.timeRemaining,
        totalTime: state.totalTime,
        generationStartTime: state.generationStartTime,
        artFormat: state.artFormat,
        contentType: state.contentType,
        isBackgroundGeneration: state.isBackgroundGeneration || false,
        generationJobId: state.generationJobId || null,
        generateContentLabel: state.generateContentLabel || "Generate Content"
      };
      safeSessionStorageSet('generationState', stateToSave);
    } catch (e) {
      console.error('Failed to save generation state:', e);
    }
  }, [safeSessionStorageSet]);

  // Helper function to clear generation state from sessionStorage
  const clearGenerationState = useCallback(() => {
    try {
      removeSessionData('generationState', PAGE_TYPE);
    } catch (e) {
      console.error('Failed to clear generation state:', e);
    }
  }, [PAGE_TYPE]);

  // Normalize and refresh credit info displayed in header without page reload
  const refreshHeaderCredits = useCallback((serverCredit) => {
    try {
      if (!serverCredit || typeof serverCredit !== 'object') {
        setCreditInfo({ total_cost: 0, credit_left_tokens: 0, credit_left_amt: 0 });
        return;
      }
      const rawAmt = serverCredit.credit_left_amt;
      const rawTokens = serverCredit.credit_left_tokens;
      const normalizedAmt = rawAmt != null ? Number(rawAmt) : (rawTokens != null ? Number(rawTokens) : 0);
      
      // Update local creditInfo state
      setCreditInfo((prev) => ({
        ...prev,
        ...serverCredit,
        credit_left_amt: Number.isFinite(normalizedAmt) ? normalizedAmt : 0,
      }));
      
      // Also update userDetails in context to keep it in sync
      setUserDetails((prev) => {
        if (!prev) return prev;
        const updatedDetails = {
          ...prev,
          credit_left_amt: Number.isFinite(normalizedAmt) ? normalizedAmt : 0,
        };
        // Persist to sessionStorage so it's available across page refreshes
        try {
          sessionStorage.setItem("userDetails", JSON.stringify(updatedDetails));
        } catch (e) {
          console.error("Failed to update sessionStorage:", e);
        }
        return updatedDetails;
      });
    } catch (_) {
      // Keep previous state on failure
    }
  }, [setUserDetails]);

  // const adjustTextareaHeight = (element) => {
  //   if (element) {
  //     element.style.height = 'auto';
  //     element.style.height = `${element.scrollHeight}px`;
  //   }
  // };

  // useEffect(() => {
  //   if (messageTextareaRef.current) {
  //     adjustTextareaHeight(messageTextareaRef.current);
  //   }
  // }, [message]);


  // Calculate dynamic container height for welcome splash and content
  useEffect(() => {
    const calculateHeight = () => {
      const viewportHeight = window.innerHeight;
      const headerHeight = 64; // Header height
      const verticalPadding = 32; // Top and bottom padding
      const bottomMargin = 24; // Small margin for breathing room
      
      const availableHeight = viewportHeight - headerHeight - verticalPadding - bottomMargin;
      setContainerHeight(Math.max(500, availableHeight)); // Minimum 500px
    };

    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => window.removeEventListener('resize', calculateHeight);
  }, []);

  // Accept pending edit image from other pages (e.g., Dashboard)
  useEffect(() => {
    const applyPendingEdit = async () => {
      try {
        setIsReferenceLoading(true);
        const pendingUrl = sessionStorage.getItem('pendingEditImageUrl');
        const pendingName = sessionStorage.getItem('pendingEditImageName') || `reference-${Date.now()}.png`;
        const pendingProductData = sessionStorage.getItem('pendingVideoProductData');
        
        if (!pendingUrl) return;

        // Clear immediately to avoid re-applying on future visits
        try {
          sessionStorage.removeItem('pendingEditImageUrl');
          sessionStorage.removeItem('pendingEditImageName');
          sessionStorage.removeItem('pendingVideoProductData');
        } catch (_) {}


        // Fetch via backend proxy to avoid CORS
        const token = await getToken();
        const proxyUrl = `${process.env.REACT_APP_API_URL}/download_media?url=${encodeURIComponent(pendingUrl)}&filename=${encodeURIComponent(pendingName)}`;
        let response;
        try {
          response = await fetch(proxyUrl, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` },
          });
        } catch (err) {
          console.error('Network error while fetching pending edit image:', err);
          toast.error('Could not load image. Please upload manually.');
          return;
        }
        if (!response.ok) {
          toast.error('Failed to fetch the image for editing.');
          return;
        }

        const contentTypeHeader = response.headers.get('Content-Type') || '';
        const blob = await response.blob();
        const blobType = contentTypeHeader || blob.type || 'image/png';
        const ext = blobType.includes('jpeg') || blobType.includes('jpg') ? 'jpg' : (blobType.includes('png') ? 'png' : 'png');
        const fileName = pendingName.includes('.') ? pendingName : `${pendingName}.${ext}`;
        const file = new File([blob], fileName, { type: blobType });

        const compressedData = await compressImageForStorage(file);
        
        // For video mode, apply product data to image object
        let productImageData = {};
        if (contentType === "video" && pendingProductData) {
          try {
            const productData = JSON.parse(pendingProductData);
            if (productData.textboxImages && productData.textboxImages.length > 0) {
              const firstImageData = productData.textboxImages[0];
              productImageData = {
                productType: firstImageData.productType,
                productItem: firstImageData.productItem,
                productView: firstImageData.productView,
                productCoverage: firstImageData.productCoverage,
                productCloseupDetail: firstImageData.productCloseupDetail,
                ProductImgDtl: firstImageData.ProductImgDtl
              };
            }
          } catch (err) {
            console.error('Error applying product data to image:', err);
          }
        }
        
        const newImageObj = {
          file,
          preview: URL.createObjectURL(file),
          dataUrl: compressedData?.dataUrl || '',
          name: file.name,
          type: file.type,
          size: file.size,
          ...productImageData
        };

        // Replace existing reference images with the new one
        setTextboxUploadedImages([newImageObj]);
        toast.success(`Loaded image into ${contentType} editor.`);
      } catch (e) {
        console.error('Error applying pending edit image:', e);
      } finally {
        setIsReferenceLoading(false);
      }
    };
    applyPendingEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // Sync the local editable content with appropriate generated content
  useEffect(() => {
    const currentContent = artFormat === "video" ? generatedVidContent : generatedImgContent;
    if (currentContent) {
      setEditedContent(currentContent.content);
    }
  }, [generatedImgContent, generatedVidContent, artFormat]);

  // Toggle edit mode: set generated image as reference for editing
  const handleToggleEdit = async () => {
    try {
      setIsReferenceLoading(true);
      const flattenedImages = Array.isArray(generatedImgHistory)
        ? generatedImgHistory.flatMap((entry) => (Array.isArray(entry.images) ? entry.images : []))
        : [];
      const singleBatch = (flattenedImages.length === 0 && Array.isArray(generatedImgContent?.images))
        ? generatedImgContent.images
        : [];
      const allImages = flattenedImages.length > 0 ? flattenedImages : singleBatch;

      if (!Array.isArray(allImages) || allImages.length === 0) {
        toast.error('No generated image available to use as reference.');
        return;
      }

      const totalImages = allImages.length;
      const effectiveIndex = (typeof sessionSelectedImageIndex === 'number' && sessionSelectedImageIndex >= 0 && sessionSelectedImageIndex < totalImages)
        ? sessionSelectedImageIndex
        : (totalImages - 1);
      const selectedItem = allImages[effectiveIndex];

      if (!selectedItem || !selectedItem.url) {
        toast.error('Could not determine the selected image.');
        return;
      }

      if (selectedItem.isVideo) {
        toast.error('Selected content is a video. Please select an image to use as a reference.');
        return;
      }

      // Fetch the image data via backend proxy to avoid CORS
      const token = await getToken();
      const tempName = `reference-${Date.now()}.png`;
      const proxyUrl = `${process.env.REACT_APP_API_URL}/download_media?url=${encodeURIComponent(selectedItem.url)}&filename=${encodeURIComponent(tempName)}`;
      let response;
      try {
        response = await fetch(proxyUrl, {
          method: 'GET',
          headers: { 'Authorization': `Bearer ${token}` },
        });
      } catch (err) {
        console.error('Network error while fetching image:', err);
        toast.error('Could not load image. Please try again.');
        return;
      }

      if (!response.ok) {
        toast.error('Failed to fetch the selected image.');
        return;
      }

      const contentTypeHeader = response.headers.get('Content-Type') || '';
      const blob = await response.blob();
      const blobType = contentTypeHeader || blob.type || 'image/png';
      const ext = blobType.includes('jpeg') || blobType.includes('jpg') ? 'jpg' : (blobType.includes('png') ? 'png' : 'png');
      const fileName = `reference-${Date.now()}.${ext}`;
      const file = new File([blob], fileName, { type: blobType });

      const compressedData = await compressImageForStorage(file);
      const newImageObj = {
        file,
        preview: URL.createObjectURL(file),
        dataUrl: compressedData?.dataUrl || '',
        name: file.name,
        type: file.type,
        size: file.size,
      };

      // Revoke previews for any existing reference images to avoid leaks
      try {
        if (Array.isArray(textboxUploadedImages)) {
          textboxUploadedImages.forEach(img => {
            if (img && img.preview) {
              try { URL.revokeObjectURL(img.preview); } catch (_) {}
            }
          });
        }
      } catch (_) {}

      // Replace existing reference images with the newly selected one
      setTextboxUploadedImages([newImageObj]);

      toast.success('Set current image as reference.');
    } catch (error) {
      console.error('Error setting reference image from generated output:', error);
      toast.error('Could not set reference image.');
    } finally {
      setIsReferenceLoading(false);
    }
  };

  // TACOSTUDIO_EOF_MARKER_12345
  // File Upload Handlers
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Allow up to 12 images for reference upload
    if (files.length > 12) {
      setReferenceUploadError("Maximum 12 images can be uploaded. Please select 12 or less images.");
      return;
    }
    
    if (referenceUploadedImages.length + files.length > 12) {
      setReferenceUploadError(`Maximum 12 images can be uploaded. You can only add ${12 - referenceUploadedImages.length} more image(s).`);
      return;
    }
    
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const invalidFiles = files.filter(file => !validImageTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      setReferenceUploadError("Please upload only JPEG, JPG, or PNG image files.");
      return;
    }
    
    setReferenceUploadError("");
    
    const filePreviews = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    
    setReferenceUploadedImages(prevImages => [...prevImages, ...filePreviews]);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeImage = (index) => {
    if (mediaSource === "original") {
      setOriginalUploadedImages((prevImages) => {
        const newImages = [...prevImages];
        URL.revokeObjectURL(newImages[index].preview);
        newImages.splice(index, 1);
        return newImages;
      });
    } else {
      setReferenceUploadedImages((prevImages) => {
        const newImages = [...prevImages];
        URL.revokeObjectURL(newImages[index].preview);
        newImages.splice(index, 1);
        return newImages;
      });
    }
  };

  const handleTextboxFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > 12) {
      setTextboxUploadError("Maximum 12 images can be uploaded. Please select 12 or less images.");
      return;
    }
    
    if (textboxUploadedImages.length + files.length > 12) {
      setTextboxUploadError(`Maximum 12 images can be uploaded. You can only add ${12 - textboxUploadedImages.length} more image(s).`);
      return;
    }
    
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    const invalidFiles = files.filter(file => !validImageTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      setTextboxUploadError("Please upload only JPEG, JPG, or PNG image files.");
      return;
    }
    
    setTextboxUploadError("");
    
    const processFiles = async () => {
      const filePreviews = await Promise.all(files.map(async (file) => {
        try {
          const compressedData = await compressImageForStorage(file);
          return {
            file,
            preview: URL.createObjectURL(file),
            dataUrl: compressedData.dataUrl,
            name: file.name,
            type: file.type,
            size: file.size
          };
        } catch (error) {
          console.error('Error compressing image:', error);
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                file,
                preview: URL.createObjectURL(file),
                dataUrl: e.target.result,
                name: file.name,
                type: file.type,
                size: file.size
              });
            };
            reader.readAsDataURL(file);
          });
        }
      }));
      
      setTextboxUploadedImages(prevImages => [...prevImages, ...filePreviews]);
    };
    
    processFiles();
    
    if (textboxFileInputRef.current) {
      textboxFileInputRef.current.value = "";
    }
  };

  const removeTextboxImage = (index) => {
    setTextboxUploadedImages((prevImages) => {
      if (!Array.isArray(prevImages) || index < 0 || index >= prevImages.length) return prevImages;
      const newImages = [...prevImages];
      const previewUrl = newImages[index]?.preview;
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  // TACOSTUDIO_EOF_MARKER_PART2
  // Polling logic for background/interrupted generation
  const pollForLatestContent = async (startTime) => {
    // Guard: Prevent multiple polling loops from running simultaneously
    if (pollingActiveRef.current) {
      console.log("Polling already active, skipping duplicate poll request");
      return;
    }
    
    console.log("Starting polling for content generated after", startTime);
    pollingActiveRef.current = true;
    
    // Polling duration: 10 minutes to be safe
    const maxPollTime = 600000; 
    const pollInterval = 5000; // 5 seconds
    const startPollTime = Date.now();

    const poll = async () => {
      // Check if generation was stopped by user or polling was deactivated
      if (!isGeneratingContentRef.current || !pollingActiveRef.current) {
          console.log("Polling stopped - generation cancelled or finished");
          pollingActiveRef.current = false;
          return; 
      }
      
      if (Date.now() - startPollTime > maxPollTime) {
        setIsGeneratingContent(false);
        isGeneratingContentRef.current = false;
        pollingActiveRef.current = false;
        // Clear generation state from sessionStorage
        clearGenerationState();
        toast.error("Generation timed out. Please check Dashboard later.");
        return;
      }

      try {
        const token = await getToken();
        const formData = new FormData();
        // userDetails is required by dashboard_data to identify user
        try {
            formData.append("userDetails", JSON.stringify(userDetails || {}));
        } catch (e) {
            console.error("Error stringifying userDetails", e);
        }
        formData.append("page", "1");
        formData.append("page_size", "5"); // Get top 5 to be safe

        const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard_data`, {
           method: "POST",
           headers: { "Authorization": `Bearer ${token}` },
           body: formData
        });

        if (response.ok) {
           const data = await response.json();
           // posts are sorted by generatedTime desc
           const latestPost = data.posts && data.posts[0];
           
           if (latestPost) {
             const postTime = new Date(latestPost.generatedTime).getTime();
             const requestTime = new Date(startTime).getTime();
             
             // Check if this post is newer than our request start time
             const expectedType = artFormat === "video" ? "video" : "image";
             
             if (postTime > requestTime && latestPost.type === expectedType) {
                // Guard: Check if this post was already processed to prevent duplicate additions
                if (lastProcessedPostTimeRef.current === latestPost.generatedTime) {
                  console.log("Content already processed, stopping polling");
                  pollingActiveRef.current = false;
                  return;
                }
                
                // IMMEDIATELY set flags to false to prevent race conditions
                // This must happen BEFORE any async state updates
                isGeneratingContentRef.current = false;
                pollingActiveRef.current = false;
                
                // Mark this post as processed to prevent duplicate additions
                lastProcessedPostTimeRef.current = latestPost.generatedTime;
                
                // Success! Found new content
                
                // Format images
                const imagesArray = [{
                    url: latestPost.signed_url,
                    prompt: latestPost.info?.["Text Prompt"] || "",
                    isVideo: latestPost.type === "video"
                }];
                
                const formattedContent = latestPost.info?.["Text Prompt"] || "";
                
                const contentObject = {
                    images: imagesArray,
                    content: formattedContent
                };

                // Update state
                if (latestPost.type === "video") {
                    setGeneratedVidContent(contentObject);
                } else {
                    setGeneratedImgContent(contentObject);
                }
                
                // Add to history with deduplication check
                const historyEntry = {
                    timestamp: latestPost.generatedTime,
                    content: formattedContent,
                    images: imagesArray
                };
                
                // Use functional update with deduplication to prevent duplicate entries
                setGeneratedImgHistory((prev) => {
                    // Check if this timestamp already exists in history
                    const isDuplicate = prev.some(entry => entry.timestamp === latestPost.generatedTime);
                    if (isDuplicate) {
                      console.log("Duplicate entry detected, skipping history addition");
                      return prev;
                    }
                    return [...prev, historyEntry];
                });

                // Save to session storage - read first to be safe
                const currentSessionData = safeSessionStorageGet('generatedContentData') || {};
                
                const dataToSave = {
                    ...currentSessionData,
                    generatedImgContent: latestPost.type !== "video" ? contentObject : (currentSessionData.generatedImgContent || generatedImgContent),
                    generatedVidContent: latestPost.type === "video" ? contentObject : (currentSessionData.generatedVidContent || generatedVidContent),
                    generatedImgHistory: [...(currentSessionData.generatedImgHistory || generatedImgHistory), historyEntry],
                    artFormat: currentSessionData.artFormat || artFormat,
                    contentMode: currentSessionData.contentMode || contentMode,
                    mediaSource: currentSessionData.mediaSource || mediaSource,
                    contentTimestamp: latestPost.generatedTime,
                    editedContent: formattedContent
                };
                
                safeSessionStorageSet('generatedContentData', dataToSave);

                setContentGenerated(true);
                setContentTimestamp(latestPost.generatedTime);
                setEditedContent(formattedContent);
                setGenerateContentLabel("Regenerate");
                
                toast.success("Content generated successfully!");
                setIsGeneratingContent(false);
                
                // Clear generation state from sessionStorage
                clearGenerationState();
                return;
             }
           }
        }
      } catch (e) {
        console.log("Polling error (retrying):", e);
      }

      // Schedule next poll only if still active
      if (pollingActiveRef.current && isGeneratingContentRef.current) {
        setTimeout(poll, pollInterval);
      }
    };

    poll();
  };

  // Main Content Generation Handler - Combined for both image and video
  const handleGenerateContent = async () => {
    // Prevent concurrent requests
    if (isGeneratingContentRef.current) {
      console.warn("Content generation already in progress. Skipping duplicate request.");
      return;
    }

    // Validate model parameters before proceeding
    if (tacoStudioEditorRef.current) {
      const validation = tacoStudioEditorRef.current.validateModelForm();
      
      if (!validation.isValid) {
        // Show first error in a toast
        const firstErrorField = Object.keys(validation.errors)[0];
        const firstErrorMessage = validation.errors[firstErrorField];
        toast.error(firstErrorMessage || "Please fill in all required fields");
        // Reset the generating flag since we're not proceeding
        isGeneratingContentRef.current = false;
        return;
      }
    }

    isGeneratingContentRef.current = true;
    pollingActiveRef.current = false; // Reset polling state for new generation
    lastProcessedPostTimeRef.current = null; // Reset processed post tracker for new generation
    setIsGeneratingContent(true);
    
    // Reset timer based on artFormat and contentType
    let newTimeRemaining;
    if (artFormat === "video") {
      // Image video: 600s (10 min), Video video: 120s (2 min)
      newTimeRemaining = contentType === "image" ? 600 : 120;
    } else {
      newTimeRemaining = 20;
    }
    setTimeRemaining(newTimeRemaining);
    setTotalTime(newTimeRemaining);
    
    // Scroll to content skeleton
    setTimeout(() => {
      if (generatedContentRef.current) {
        generatedContentRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 30);
    
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    
    // Reset error states
    setMessageError("");
    setReferenceUploadError("");
    setOwnImageUploadError("");
    setApiError("");

    // Determine video duration based on content type
    const effectiveReelDuration = contentType === "image" ? 10 : reelDuration;
    
    // Clear existing content if regenerating
    if (generateContentLabel === "Regenerate") {
      if (artFormat === "video") {
        setGeneratedVidContent(null);
      } else {
        setGeneratedImgContent(null);
      }
      setContentGenerated(false);
      setSelectedImages([]);
    }
    
    // Get current message from textarea
    // const currentMessage = messageTextareaRef.current && typeof messageTextareaRef.current.value === 'string'
    //   ? messageTextareaRef.current.value
    //   : message;
    // const effectiveMessage = (currentMessage || '').trim();

    const generationStartTime = new Date();
    
    // Save generation state to sessionStorage
    saveGenerationState({
      isGeneratingContent: true,
      timeRemaining: newTimeRemaining,
      totalTime: newTimeRemaining,
      generationStartTime: generationStartTime.toISOString(),
      artFormat,
      contentType,
      isBackgroundGeneration: false,
      generationJobId: null,
      generateContentLabel: "Generating..."
    });

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("email", user?.primaryEmailAddress?.emailAddress);
      formDataToSend.append("artFormat", artFormat);
      // formDataToSend.append("message", effectiveMessage);
      
      try {
        formDataToSend.append("userDetails", JSON.stringify(userDetails || {}));
      } catch (e) {}
      
      // Add textbox uploaded images to form data
      // if (textboxUploadedImages.length > 0) {
      //   textboxUploadedImages.forEach((img, index) => {
      //     formDataToSend.append(`reference_img_${index}`, img.file);
      //     if (img.maskBlob) {
      //       formDataToSend.append(`reference_mask_${index}`, img.maskBlob, `mask_${index}.png`);
      //       if (img.maskOriginalSize) {
      //         try {
      //           formDataToSend.append(`reference_mask_meta_${index}`, JSON.stringify(img.maskOriginalSize));
      //         } catch (_e) {}
      //       }
      //     }
      //   });
      //   formDataToSend.append("reference_img_count", textboxUploadedImages.length.toString());
      // }
      
      // Add model parameters if a model with schema is selected
      if (selectedModel && selectedModel !== 'free' && selectedModel !== 'auto') {
        formDataToSend.append("model_id", selectedModel);
        
        // Optimized parameter handler - separates file handling from value handling
        const appendFileParameter = (key, value) => {
          if (!value) return;
          
          // Handle single File object
          if (value instanceof File) {
            formDataToSend.append(`model_${key}`, value);
            return;
          }
          
          // Handle array of Files
          if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
            value.forEach((file, index) => {
              if (file instanceof File) {
                formDataToSend.append(`model_${key}_${index}`, file);
              }
            });
            formDataToSend.append(`model_${key}_count`, value.length.toString());
          }
        };
        
        const appendValueParameter = (key, value) => {
          if (value === undefined || value === null || value === '') {
            return; // Skip empty values
          }
          
          // Skip File objects in value parameters (they should be in modelFileObjects)
          if (value instanceof File || (Array.isArray(value) && value.length > 0 && value[0] instanceof File)) {
            return;
          }
          
          // Handle objects and arrays (non-File)
          if (typeof value === 'object') {
            formDataToSend.append(`model_${key}`, JSON.stringify(value));
            return;
          }
          
          // Handle primitives (string, number, boolean)
          formDataToSend.append(`model_${key}`, String(value));
        };
        
        // Process model parameters (non-file values only)
        if (modelParams && typeof modelParams === 'object') {
          Object.entries(modelParams).forEach(([key, value]) => {
            appendValueParameter(key, value);
          });
        }
        
        // Process model file objects (files only)
        if (modelFileObjects && typeof modelFileObjects === 'object') {
          Object.entries(modelFileObjects).forEach(([key, value]) => {
            appendFileParameter(key, value);
          });
        }
      }
      
      console.log("userDetails:", userDetails);

      // Log request size and composition for debugging 413 errors
      let totalFileSize = 0;
      let fileCount = 0;
      let formEntryCount = 0;
      let userDetailsSize = 0;
      let modelParamsSize = 0;
      
      // Calculate sizes (FormData doesn't expose size directly, so we estimate)
      try {
        const userDetailsStr = JSON.stringify(userDetails || {});
        userDetailsSize = new Blob([userDetailsStr]).size;
      } catch (e) {
        console.warn("Could not calculate userDetails size:", e);
      }
      
      // Count and estimate file sizes
      if (modelFileObjects && typeof modelFileObjects === 'object') {
        Object.entries(modelFileObjects).forEach(([key, value]) => {
          if (value instanceof File) {
            fileCount++;
            totalFileSize += value.size;
            console.log(`[REQUEST_SIZE_DEBUG] File in modelFileObjects: ${key}, size: ${(value.size / 1024).toFixed(2)} KB, name: ${value.name}`);
          } else if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
            value.forEach((file, idx) => {
              if (file instanceof File) {
                fileCount++;
                totalFileSize += file.size;
                console.log(`[REQUEST_SIZE_DEBUG] File in modelFileObjects array: ${key}[${idx}], size: ${(file.size / 1024).toFixed(2)} KB, name: ${file.name}`);
              }
            });
          }
        });
      }
      
      // Estimate model params size
      if (modelParams && typeof modelParams === 'object') {
        try {
          const modelParamsStr = JSON.stringify(modelParams);
          modelParamsSize = new Blob([modelParamsStr]).size;
        } catch (e) {
          console.warn("Could not calculate modelParams size:", e);
        }
      }
      
      // Count form entries (approximate)
      formEntryCount = 2; // email, artFormat
      if (userDetails) formEntryCount++;
      if (selectedModel && selectedModel !== 'free' && selectedModel !== 'auto') {
        formEntryCount++; // model_id
        if (modelParams) formEntryCount += Object.keys(modelParams).length;
        if (modelFileObjects) formEntryCount += Object.keys(modelFileObjects).length;
      }
      
      const estimatedTotalSize = userDetailsSize + modelParamsSize + totalFileSize;
      console.log(`[REQUEST_SIZE_DEBUG] Request composition:`, {
        fileCount,
        totalFileSizeKB: (totalFileSize / 1024).toFixed(2),
        totalFileSizeMB: (totalFileSize / (1024 * 1024)).toFixed(2),
        userDetailsSizeKB: (userDetailsSize / 1024).toFixed(2),
        modelParamsSizeKB: (modelParamsSize / 1024).toFixed(2),
        estimatedTotalSizeKB: (estimatedTotalSize / 1024).toFixed(2),
        estimatedTotalSizeMB: (estimatedTotalSize / (1024 * 1024)).toFixed(2),
        formEntryCount,
        modelFileObjectsKeys: modelFileObjects ? Object.keys(modelFileObjects) : [],
        modelParamsKeys: modelParams ? Object.keys(modelParams) : []
      });
      
      console.log("formDataToSend:", formDataToSend);
      
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/generate_content_studio`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`
          },
          body: formDataToSend,
          signal: abortControllerRef.current?.signal
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate content");
      }
      
      const data = await response.json();
      
      // Parse images
      let imagesMap = {};
      try {
        imagesMap = typeof data.images === 'string' ? JSON.parse(data.images) : data.images;
      } catch {
        imagesMap = data.images || {};
      }
      
      // Parse content
      let formattedContent = "";
      try {
        if (typeof data.content === 'string') {
          try {
            formattedContent = JSON.parse(data.content);
          } catch {
            const match = data.content.match(/\"caption\":\s*\"([^\"]*)\"/);
            if (match) {
              formattedContent = match[1];
            } else {
              formattedContent = data.content;
            }
          }
        } else {
          formattedContent = data.content;
        }
      } catch {
        formattedContent = data.content;
      }
      
      // Format images into array
      const imagesArray = [];
      if (typeof imagesMap === 'object' && imagesMap !== null) {
        Object.entries(imagesMap).forEach(([prompt, urls]) => {
          const urlArray = Array.isArray(urls) ? urls : [urls];
          urlArray.forEach(url => {
            if (url) {
              imagesArray.push({
                url,
                prompt,
                isVideo: artFormat === "video"
              });
            }
          });
        });
      }
      
      const contentObject = {
        images: imagesArray,
        content: formattedContent
      };
      
      // Update state based on post type
      if (artFormat === "video") {
        setGeneratedVidContent(contentObject);
      } else {
        setGeneratedImgContent(contentObject);
      }
      
      // Add to history
      const historyEntry = {
        timestamp: data.timestamp || new Date().toISOString(),
        content: formattedContent,
        images: imagesArray
      };
      setGeneratedImgHistory((prev) => [...prev, historyEntry]);
      
      // Save to session storage
      const dataToSave = {
        generatedImgContent: artFormat !== "video" ? contentObject : generatedImgContent,
        generatedVidContent: artFormat === "video" ? contentObject : generatedVidContent,
        generatedImgHistory: [...generatedImgHistory, historyEntry],
        artFormat,
        contentMode,
        mediaSource,
        selectedImages: [],
        selectedImageIndex: null,
        contentTimestamp: data.timestamp || new Date().toISOString(),
        editedContent: formattedContent
      };
      
      safeSessionStorageSet('generatedContentData', dataToSave);
      
      setContentGenerated(true);
      setContentTimestamp(data.timestamp || "");
      setEditedContent(formattedContent);
      setGenerateContentLabel("Regenerate");
      
      // Update credit info if available - replicated from CreateImage.js
      const credit = data && { ...data.credit, time_taken_sec: data.time_taken_sec };
      // console.log("Credit:", credit);
      
      // Safely set credit info with fallback to default values if credit is null or invalid
      if (credit && typeof credit === 'object') {
        refreshHeaderCredits(credit);
        setHasApiCredits(true);
      } else {
        refreshHeaderCredits({ total_cost: 0, credit_left_tokens: 0 });
      }
      
      toast.success("Content generated successfully!");
      
      setIsGeneratingContent(false);
      isGeneratingContentRef.current = false;
      pollingActiveRef.current = false;
      
      // Clear generation state from sessionStorage
      clearGenerationState();
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log("Generation was cancelled by user");
        setIsGeneratingContent(false);
        isGeneratingContentRef.current = false;
        pollingActiveRef.current = false;
        // Clear generation state from sessionStorage
        clearGenerationState();
      } else {
        console.error("Error generating content:", error);
        
        // Check for network errors or "Load Failed" which happens on mobile backgrounding
        const isNetworkError = 
          error.message === "Failed to fetch" || 
          error.message === "Load failed" || 
          (error.message && error.message.includes("NetworkError")) ||
          (error.message && error.message.includes("Load Failed"));

        if (isNetworkError) {
             console.log("Network error detected (likely backgrounded). Switching to polling.");
             // Don't show error, keep spinning
             // Update generation state to indicate background generation
             saveGenerationState({
               isGeneratingContent: true,
               timeRemaining,
               totalTime,
               generationStartTime: generationStartTime.toISOString(),
               artFormat,
               contentType,
               isBackgroundGeneration: true,
               generationJobId: Date.now().toString(),
               generateContentLabel: "Generating..."
             });
             pollForLatestContent(generationStartTime);
             // Do NOT set isGeneratingContent(false) here, let polling handle it
        } else {
            setApiError(error.message || "Failed to generate content");
            toast.error(error.message || "Failed to generate content");
            
            // For video mode, handle background generation fallback
            if (contentType === "video" && error.message && error.message.includes('network')) {
              setIsBackgroundGeneration(true);
              setGenerationJobId(Date.now().toString()); // Mock job ID
              // Save state for background generation
              saveGenerationState({
                isGeneratingContent: true,
                timeRemaining,
                totalTime,
                generationStartTime: generationStartTime.toISOString(),
                artFormat,
                contentType,
                isBackgroundGeneration: true,
                generationJobId: Date.now().toString(),
                generateContentLabel: "Generating..."
              });
            } else {
              // Clear generation state on error (non-network errors)
              clearGenerationState();
            }
            
            setIsGeneratingContent(false);
            isGeneratingContentRef.current = false;
            pollingActiveRef.current = false;
        }
      }
    } 
    // Note: finally block removed to support polling continuation
  };

  // Stop Generation Handler
  const handleStopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    isGeneratingContentRef.current = false;
    pollingActiveRef.current = false;
    setIsGeneratingContent(false);
    setGenerateContentLabel("Generate Content");
    // Reset timer based on artFormat and contentType
    const newTotalTime = artFormat === "video" ? (contentType === "image" ? 600 : 120) : 20;
    setTimeRemaining(newTotalTime);
    setTotalTime(newTotalTime);
    // Clear generation state from sessionStorage
    clearGenerationState();
  };

  // Clear Content Handler
  const handleClearContent = () => {
    // Abort any ongoing generation
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    // Clear session storage for this content type
    removeSessionData('generatedContentData', PAGE_TYPE);
    removeSessionData('landingPageContent', PAGE_TYPE);
    // Reset all states
    setGeneratedImgContent(null);
    setGeneratedVidContent(null);
    setContentGenerated(false);
    setIsGeneratingContent(false);
    isGeneratingContentRef.current = false;
    pollingActiveRef.current = false;
    lastProcessedPostTimeRef.current = null; // Reset processed post tracker for new generation
    setGenerateContentLabel("Generate Content");
    setSelectedImages([]);
    setSelectedImageIndex(null);
    setIsEditingContent(false);
    setEditedContent("");
    setGeneratedImgHistory([]);
    setSessionSelectedImageIndex(null);
    setContentTimestamp("");
    setHasApiCredits(false); // Reset API credits flag to match CreateImage.js
    
    // Preserve some form values but clear images
    // Don't reset: artFormat, aspectRatio, message, textboxUploadedImages
    
    // Revoke blob URLs to prevent memory leaks
    originalUploadedImages.forEach(img => {
      if (img.preview) URL.revokeObjectURL(img.preview);
    });
    referenceUploadedImages.forEach(img => {
      if (img.preview) URL.revokeObjectURL(img.preview);
    });
    
    setOriginalUploadedImages([]);
    setReferenceUploadedImages([]);
    
    // Clear file inputs
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (textboxFileInputRef.current) {
      textboxFileInputRef.current.value = "";
    }
    
    toast.success("Content cleared");
  };

  // Image Selection Handler
  const handleImageSelect = (index) => {
    setSelectedImages(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
    setSelectedImageIndex(index);
  };

  // Publish Handler
  const handlePublish = () => {
    const email = user?.primaryEmailAddress?.emailAddress;
    if (!email) {
      toast.error("User email not found");
      return;
    }
    
    const currentContent = artFormat === "video" ? generatedVidContent : generatedImgContent;
    if (!currentContent || !currentContent.images || currentContent.images.length === 0) {
      toast.error("No content to publish");
      return;
    }
    
    if (selectedImages.length === 0) {
      toast.error("Please select at least one image/video to publish");
      return;
    }
    
    const url = `/instagram-connect?email=${encodeURIComponent(email)}&timestamp=${encodeURIComponent(contentTimestamp)}&selectedImages=${encodeURIComponent(JSON.stringify(selectedImages))}`;
    window.open(url, '_blank', 'width=600,height=800');
  };

  // Schedule Later Handler
  const handleScheduleLater = () => {
    const email = user?.primaryEmailAddress?.emailAddress;
    if (!email) {
      toast.error("User email not found");
      return;
    }
    
    const currentContent = artFormat === "video" ? generatedVidContent : generatedImgContent;
    if (!currentContent || !currentContent.images || currentContent.images.length === 0) {
      toast.error("No content to schedule");
      return;
    }
    
    if (selectedImages.length === 0) {
      toast.error("Please select at least one image/video to schedule");
      return;
    }
    
    navigate(`/schedule?email=${encodeURIComponent(email)}&timestamp=${encodeURIComponent(contentTimestamp)}&selectedImages=${encodeURIComponent(JSON.stringify(selectedImages))}`);
  };

  // Media Source Change Handler
  const handleMediaSourceChange = (source) => {
    setMediaSource(source);
    setUploadError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (source === "generate") {
      setOriginalUploadedImages([]);
      setReferenceUploadedImages([]);
    } else if (source === "original") {
      setReferenceUploadedImages([]);
    } else if (source === "reference") {
      setOriginalUploadedImages([]);
    }
  };

  // Aspect Ratio Change Handler
  const handleAspectRatioChange = (ratio) => {
    setAspectRatio(ratio);
    setIsAspectRatioExpanded(false);
  };


  // Lightbox handlers
  const openLightbox = useCallback((event, { photo, index }) => {
    setCurrentLightboxImage(index);
    setViewerIsOpen(true);
  }, []);

  const closeLightbox = () => {
    setViewerIsOpen(false);
  };

  // Handle example selection from welcome splash
  const handleExampleSelect = useCallback((exampleData) => {
    // Load the example's settings into the editor
    if (exampleData.model) {
      setSelectedModel(exampleData.model);
    }
    if (exampleData.aspectRatio) {
      setAspectRatio(exampleData.aspectRatio);
    }
    // Set the prompt if using text-to-image mode
    if (exampleData.prompt) {
      setMessage(exampleData.prompt);
    }
    
    // Scroll to editor/prompt area to show loaded settings
    setTimeout(() => {
      if (tacoStudioEditorRef.current) {
        const editorElement = tacoStudioEditorRef.current;
        if (editorElement.scrollIntoView) {
          editorElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }, 100);
    
    toast.success('Example loaded! Adjust settings and hit Generate.');
  }, []);

  // Handle "Start Creating" button from welcome splash
  const handleStartCreating = useCallback(() => {
    // Scroll to editor area
    if (tacoStudioEditorRef.current) {
      const editorElement = tacoStudioEditorRef.current;
      if (editorElement.scrollIntoView) {
        editorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, []);

  // TACOSTUDIO_EOF_MARKER_PART3
  // Load saved content from sessionStorage when component mounts or content type changes
  useEffect(() => {
    try {
      // First check for content from landing page
      const landingPageContent = safeSessionStorageGet('landingPageContent');
      if (landingPageContent) {
        setIsLoadingFromLandingPage(true);
        
        if (landingPageContent.message && landingPageContent.message.trim()) {
          setMessage(landingPageContent.message);
        }
        if (landingPageContent.artFormat) {
          setArtFormat(landingPageContent.artFormat);
        }
        
        setTimeout(() => {
          setIsLoadingFromLandingPage(false);
          removeSessionData('landingPageContent', PAGE_TYPE);
        }, 100);
        return;
      }

      // Otherwise load saved generated content
      const savedData = safeSessionStorageGet('generatedContentData');
      if (savedData) {
        // Restore form state
        if (savedData.artFormat) setArtFormat(savedData.artFormat);
        if (savedData.contentMode) setContentMode(savedData.contentMode);
        if (savedData.mediaSource) setMediaSource(savedData.mediaSource);
        if (savedData.message) setMessage(savedData.message);
        
        // Restore generated content
        if (savedData.generatedImgContent) setGeneratedImgContent(savedData.generatedImgContent);
        if (savedData.generatedVidContent) setGeneratedVidContent(savedData.generatedVidContent);
        if (savedData.generatedImgHistory) setGeneratedImgHistory(savedData.generatedImgHistory);
        if (savedData.sessionSelectedImageIndex !== undefined) setSessionSelectedImageIndex(savedData.sessionSelectedImageIndex);
        
        // Restore images from compressed data
        if (savedData.textboxImages && Array.isArray(savedData.textboxImages)) {
          const restoredImages = savedData.textboxImages.map(img => {
            if (img.dataUrl) {
              const byteString = atob(img.dataUrl.split(',')[1]);
              const mimeString = img.dataUrl.split(',')[0].split(':')[1].split(';')[0];
              const ab = new ArrayBuffer(byteString.length);
              const ia = new Uint8Array(ab);
              for (let i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i);
              }
              const blob = new Blob([ab], { type: mimeString });
              const file = new File([blob], img.name || 'image.jpg', { type: mimeString });
              
              return {
                file,
                preview: URL.createObjectURL(blob),
                dataUrl: img.dataUrl,
                name: img.name,
                type: img.type,
                size: img.size,
                ProductImgDtl: img.ProductImgDtl
              };
            }
            return null;
          }).filter(Boolean);
          
          setTextboxUploadedImages(restoredImages);
        }
        
        
        // Restore selection and editing state
        if (savedData.selectedImages) setSelectedImages(savedData.selectedImages);
        if (savedData.selectedImageIndex !== undefined) setSelectedImageIndex(savedData.selectedImageIndex);
        if (savedData.contentTimestamp) setContentTimestamp(savedData.contentTimestamp);
        if (savedData.editedContent) setEditedContent(savedData.editedContent);
        
        // Set flags
        if (savedData.generatedImgContent || savedData.generatedVidContent) {
          setContentGenerated(true);
          setGenerateContentLabel("Regenerate");
        }
      }
      
      // Restore generation state if generation was in progress
      const savedGenerationState = safeSessionStorageGet('generationState');
      if (savedGenerationState && savedGenerationState.isGeneratingContent) {
        // Restore generation state
        setIsGeneratingContent(true);
        isGeneratingContentRef.current = true;
        
        if (savedGenerationState.timeRemaining !== undefined) {
          setTimeRemaining(savedGenerationState.timeRemaining);
        }
        if (savedGenerationState.totalTime !== undefined) {
          setTotalTime(savedGenerationState.totalTime);
        }
        if (savedGenerationState.generateContentLabel) {
          setGenerateContentLabel(savedGenerationState.generateContentLabel);
        }
        if (savedGenerationState.isBackgroundGeneration) {
          setIsBackgroundGeneration(true);
        }
        if (savedGenerationState.generationJobId) {
          setGenerationJobId(savedGenerationState.generationJobId);
        }
        
        // Resume polling if generation was in progress and we have a start time
        if (savedGenerationState.generationStartTime) {
          const startTime = new Date(savedGenerationState.generationStartTime);
          // Check if generation is still within reasonable time (e.g., 15 minutes)
          const timeSinceStart = Date.now() - startTime.getTime();
          const maxGenerationTime = 15 * 60 * 1000; // 15 minutes
          
          // Calculate remaining time based on elapsed time
          if (savedGenerationState.totalTime && timeSinceStart < maxGenerationTime) {
            const elapsedSeconds = Math.floor(timeSinceStart / 1000);
            const calculatedRemaining = Math.max(0, savedGenerationState.totalTime - elapsedSeconds);
            setTimeRemaining(calculatedRemaining);
            
            // Update saved state with calculated remaining time
            try {
              saveGenerationState({
                ...savedGenerationState,
                timeRemaining: calculatedRemaining
              });
            } catch (e) {
              // Ignore errors
            }
          }
          
          if (timeSinceStart < maxGenerationTime) {
            // Resume polling
            pollForLatestContent(startTime);
          } else {
            // Generation timed out, clear state
            setIsGeneratingContent(false);
            isGeneratingContentRef.current = false;
            pollingActiveRef.current = false;
            clearGenerationState();
            toast.error("Previous generation timed out. Please start a new generation.");
          }
        }
      }
    } catch (error) {
      console.error("Error loading from sessionStorage:", error);
    }
    // NOTE: pollForLatestContent intentionally NOT in deps - it's not memoized and would cause infinite loops
    // This effect should only run once on mount to restore saved state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PAGE_TYPE]); // Load once on mount - other deps are refs or stable callbacks

  // Update time remaining based on post type and content type
  useEffect(() => {
    if (artFormat === "video") {
      // Image video: 600s (10 min), Video video: 120s (2 min)
      const newTime = contentType === "image" ? 600 : 120;
      setTimeRemaining(newTime);
      setTotalTime(newTime);
    } else {
      setTimeRemaining(20);
      setTotalTime(20);
    }
  }, [artFormat, contentType]);

  // Countdown timer - also persist state updates
  useEffect(() => {
    if (isGeneratingContent && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          const newValue = Math.max(0, prev - 1);
          // Update sessionStorage with new time remaining (debounced to avoid excessive writes)
          if (isGeneratingContentRef.current && newValue % 5 === 0) {
            // Only update sessionStorage every 5 seconds to avoid excessive writes
            try {
              const savedState = safeSessionStorageGet('generationState');
              if (savedState) {
                saveGenerationState({
                  ...savedState,
                  timeRemaining: newValue
                });
              }
            } catch (e) {
              // Ignore errors
            }
          }
          return newValue;
        });
      }, 1000);
      return () => clearInterval(timer);
    } else if (isGeneratingContent && timeRemaining === 0) {
      // Timer reached zero, but generation might still be in progress (polling)
      // Don't clear state here, let polling handle completion
    }
  }, [isGeneratingContent, timeRemaining, saveGenerationState, safeSessionStorageGet]);

  // Scroll to content when generation starts
  useEffect(() => {
    if (isGeneratingContent) {
      setTimeout(() => {
        if (generatedContentRef.current) {
          generatedContentRef.current.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 20);
    }
  }, [isGeneratingContent]);

  // Video-specific: Page visibility handling
  useEffect(() => {
    if (contentType !== "video") return;
    
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      setIsPageVisible(isVisible);
      if (isVisible && isBackgroundGeneration && generationJobId) {
        // Check generation status when page becomes visible
        // checkGenerationStatus(); // Implement if needed
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [contentType, isBackgroundGeneration, generationJobId]);

  // Video-specific: Show server generation message after 30s
  // useEffect(() => {
  //   if (contentType !== "video") return;
    
  //   let timeoutId;
  //   if (isGeneratingContent && !isBackgroundGeneration) {
  //     timeoutId = setTimeout(() => {
  //       setShowServerGenerationMessage(true);
  //     }, 30000);
  //   }
  //   return () => {
  //     if (timeoutId) clearTimeout(timeoutId);
  //   };
  // }, [contentType, isGeneratingContent, isBackgroundGeneration]);

  // Dismiss error popups when clicking outside
  useEffect(() => {
    const handleGlobalClick = (event) => {
      const isLowCreditsError = (errMsg) => {
        try {
          return typeof errMsg === 'string' && errMsg.toLowerCase().includes('low credit');
        } catch (_) {
          return false;
        }
      };
      
      const anyErrorVisible = Boolean(
        messageError ||
        textboxUploadError ||
        apiError
      );
      if (!anyErrorVisible) return;

      if (errorPopupRef.current && !errorPopupRef.current.contains(event.target)) {
        setMessageError("");
        setTextboxUploadError("");
        setApiError("");
      }
    };

    document.addEventListener('mousedown', handleGlobalClick);
    return () => document.removeEventListener('mousedown', handleGlobalClick);
  }, [messageError, textboxUploadError, apiError]);

  // Track current images for cleanup
  const textboxImagesRef = useRef([]);
  useEffect(() => {
    textboxImagesRef.current = textboxUploadedImages;
  }, [textboxUploadedImages]);

  // Cleanup blob URLs on component unmount only
  useEffect(() => {
    return () => {
      textboxImagesRef.current.forEach(img => {
        if (img?.preview) {
          try {
            URL.revokeObjectURL(img.preview);
          } catch (e) {
            // Ignore errors during cleanup
          }
        }
      });
    };
  }, []);


  // TACOSTUDIO_EOF_MARKER_PART4
  // Helper to check if error is low credits
  const isLowCreditsError = (errMsg) => {
    try {
      return typeof errMsg === 'string' && errMsg.toLowerCase().includes('low credit');
    } catch (_) {
      return false;
    }
  };

  // Calculate credit balance
  const creditBalance = parseFloat(hasApiCredits ? (creditInfo?.credit_left_amt ?? '0') : (userDetails?.credit_left_amt ?? '0'));

  return (
    <div className="min-h-screen flex flex-col justify-start items-center lg:items-stretch relative bg-gradient-to-br from-black via-gray-900 to-black overflow-y-auto">
      {/* Header */}
      <CreateContentHeader creditInfo={creditInfo} hasApiCredits={hasApiCredits} onHelpClick={() => {}} contentType={contentType} />
      
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-10 sm:top-16 md:top-20 left-4 sm:left-10 md:left-20 w-32 sm:w-48 md:w-64 h-32 sm:h-48 md:h-64 bg-gradient-to-br from-purple-600/20 to-pink-600/20 rounded-full blur-2xl sm:blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-10 sm:bottom-16 md:bottom-20 right-4 sm:right-10 md:right-20 w-48 sm:w-72 md:w-96 h-48 sm:h-72 md:h-96 bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-full blur-2xl sm:blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
      </div>

      {/* Main content with consistent spacing */}
      <div className="relative z-10 w-full max-w-7xl lg:max-w-none mx-auto px-4 sm:px-6 lg:px-6 xl:px-8 flex flex-col pt-12 pb-20 sm:pb-24 md:pb-28">
        
        {/* Workspace Layout */}
        <div className={`flex flex-col lg:flex-row gap-6 transition-all duration-300 lg:min-h-[calc(100vh-12rem)]`}>
        {/* <div className={`flex flex-col gap-6 transition-all duration-300`}> */}

          {/* Generated Content Area */}
          <div className="flex-1 w-full flex flex-col lg:min-w-0">
            
            {/* Generated Content Section - Centered and Responsive */}
            <div className="flex-1 flex flex-col items-stretch lg:items-stretch justify-start">
              
            {/* Generated Content Section - Full width on desktop */}
            <div className="w-full flex justify-center lg:justify-stretch" ref={generatedContentRef}>
              {/* Show Welcome Splash when no content and not generating */}
              {!contentGenerated && !isGeneratingContent && !isBackgroundGeneration ? (
                <TacoStudioWelcomeSplash
                  onExampleSelect={handleExampleSelect}
                  onStartCreating={handleStartCreating}
                  containerHeight={containerHeight}
                />
              ) : (
                <GeneratedContent
                  contentType={contentType}
                  isGeneratingContent={isGeneratingContent}
                  contentGenerated={contentGenerated}
                  generatedImgContent={generatedImgContent}
                  generatedVidContent={generatedVidContent}
                  artFormat={artFormat}
                  timeRemaining={timeRemaining}
                  selectedImages={selectedImages}
                  isEditingContent={isEditingContent}
                  editedContent={editedContent}
                  setEditedContent={setEditedContent}
                  isPlaying={isPlaying}
                  setIsPlaying={setIsPlaying}
                  isPublishing={isPublishing}
                  publishStatus={publishStatus}
                  contentTimestamp={contentTimestamp}
                  viewerIsOpen={viewerIsOpen}
                  currentLightboxImage={currentLightboxImage}
                  creditInfo={creditInfo}
                  user={user}
                  handleStopGeneration={handleStopGeneration}
                  handleToggleEdit={handleToggleEdit}
                  handlePublish={handlePublish}
                  handleScheduleLater={handleScheduleLater}
                  openLightbox={openLightbox}
                  closeLightbox={closeLightbox}
                  handleClearContent={handleClearContent}
                  textboxUploadedImages={textboxUploadedImages}
                  generatedImgHistory={generatedImgHistory}
                  sessionSelectedImageIndex={sessionSelectedImageIndex}
                  setSessionSelectedImageIndex={setSessionSelectedImageIndex}
                  selectedCreationOption={selectedCreationOption}
                  aspectRatio={aspectRatio}
                />
              )}
            </div>

            {/* Dashboard Info Message - Shown after first content generation */}
            {contentGenerated && (
              <div className="w-full flex justify-center mt-4 mb-2">
                <div className="glass-effect border border-white/10 rounded-lg px-4 py-2.5 flex items-center gap-3 max-w-2xl">
                  <FaInfoCircle className="text-blue-400 flex-shrink-0" size={16} />
                  <p className="text-gray-300 text-sm flex-1">
                    View all your generated content in the Dashboard
                  </p>
                  <button
                    onClick={() => navigate('/Dashboard')}
                    className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-xs font-medium rounded-lg transition-all duration-300 transform hover:scale-105 whitespace-nowrap"
                  >
                    Go to Dashboard
                  </button>
                </div>
              </div>
            )}


            {/* Error Messages */}
            {(messageError || textboxUploadError || apiError) && (
              <div className="fixed inset-0 z-[60] flex items-start justify-center pt-20 pointer-events-none">
                <div ref={errorPopupRef} className="pointer-events-auto">
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-effect border border-red-500/20 rounded-xl p-6 max-w-md mx-4"
                  >
                    <div className="flex items-start gap-3">
                      <FiAlertCircle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
                      <div className="flex-1">
                        <h3 className="text-red-400 font-semibold mb-2">Error</h3>
                        <p className="text-gray-300 text-sm">
                          {messageError || textboxUploadError || apiError}
                        </p>
                        {(isLowCreditsError(messageError) || isLowCreditsError(apiError)) && (
                          <button
                            onClick={() => navigate('/billing')}
                            className="mt-3 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition-all"
                          >
                            Buy Credits
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            )}


            </div>
          </div>

          {/* Editor Sidebar - Unified for both Image and Video */}
          <div className="w-full lg:w-[320px] xl:w-[360px] lg:flex-shrink-0 lg:sticky lg:top-16 lg:self-start lg:overflow-y-auto lg:max-h-[calc(100vh-5rem)]">
            <TacoStudioEditor
              ref={tacoStudioEditorRef}
              creditBalance={creditBalance}
              contentType={contentType}
              handleContentTypeChange={handleContentTypeChange}
              selectedPlatform={selectedPlatform}
              setSelectedPlatform={setSelectedPlatform}
              artFormat={artFormat}
              setArtFormat={setArtFormat}
              contentMode={contentMode}
              setContentMode={setContentMode}
              mediaSource={mediaSource}
              handleMediaSourceChange={handleMediaSourceChange}
              message={message}
              setMessage={setMessage}
              // messageTextareaRef={messageTextareaRef}
              messageError={messageError}
              handleGenerateContent={handleGenerateContent}
              isGeneratingContent={isGeneratingContent}
              contentGenerated={contentGenerated}
              enableGenerateContentButton={enableGenerateContentButton}
              generateContentLabel={generateContentLabel}
              handleFileUpload={handleFileUpload}
              fileInputRef={fileInputRef}
              originalUploadedImages={originalUploadedImages}
              referenceUploadedImages={referenceUploadedImages}
              removeImage={removeImage}
              uploadError={uploadError}
              referenceUploadError={referenceUploadError}
              ownImageUploadError={ownImageUploadError}
              aspectRatio={aspectRatio}
              handleAspectRatioChange={handleAspectRatioChange}
              selectedProduct={selectedProduct}
              setSelectedProduct={setSelectedProduct}
              selectedEvent={selectedEvent}
              setSelectedEvent={setSelectedEvent}
              selectedStyle={selectedStyle}
              setSelectedStyle={setSelectedStyle}
              textboxUploadedImages={textboxUploadedImages}
              setTextboxUploadedImages={setTextboxUploadedImages}
              textboxUploadError={textboxUploadError}
              handleTextboxFileUpload={handleTextboxFileUpload}
              removeTextboxImage={removeTextboxImage}
              textboxFileInputRef={textboxFileInputRef}
              isReferenceLoading={isReferenceLoading}
              creationInputMode={creationInputMode}
              setCreationInputMode={setCreationInputMode}
              selectedCreationOption={selectedCreationOption}
              setSelectedCreationOption={setSelectedCreationOption}
              selectedModel={selectedModel}
              setSelectedModel={setSelectedModel}
              showReferenceImageModal={showReferenceImageModal}
              setShowReferenceImageModal={setShowReferenceImageModal}
              isModelCollapsed={isModelCollapsed}
              setIsModelCollapsed={setIsModelCollapsed}
              isOutputCollapsed={isOutputCollapsed}
              setIsOutputCollapsed={setIsOutputCollapsed}
              isSidebarCollapsed={isSidebarCollapsed}
              setIsSidebarCollapsed={setIsSidebarCollapsed}
              modelParams={modelParams}
              setModelParams={setModelParams}
              modelFileObjects={modelFileObjects}
              setModelFileObjects={setModelFileObjects}
            />
          </div>

        </div>
      </div>
    </div>
  );
}

export default TacoStudio;
